import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V1_URL, VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";

export interface ExtractionState {
  stage?: "uploaded" | "processing" | "extracted" | "failed" | string;
  status?: string;
  has_detail?: boolean;
  has_fields?: boolean;
  partial?: boolean;
  processing_error?: string | null;
}

export interface DocumentVerificationBundleDocument {
  id?: string;
  document_id?: string;
  document_role?: string | null;
  document_type?: string | null;
  required_document_id?: string | null;
  file_name?: string | null;
  preview_url?: string | null;
  download_url?: string | null;
  url?: string | null;
  mime_type?: string | null;
  file_size?: number | null;
  upload_date?: string | null;
  request_source?: string | null;
  extraction_state?: ExtractionState | null;
  extraction?: {
    status?: string | null;
  } | null;
  detail?: Record<string, any> | null;
  fields?: Record<string, any> | null;
  [key: string]: any;
}

export interface DocumentVerificationBundleContext {
  account_id?: string;
  partner_id?: string;
  map_id?: string;
  invoice_id?: string;
}

interface FetchOcrDataRequest {
  file: any;
  extraction_mode?: string;
  source?: string;
  bundle_id?: string;
  document_role?: string;
  onUploadComplete?: () => void;
}

export interface DocumentVerificationBundleResponse {
  id?: string;
  bundle_id?: string;
  status?: string | null;
  source?: string | null;
  extraction_state?: ExtractionState | null;
  documents?: DocumentVerificationBundleDocument[] | null;
  documents_by_role?: Record<string, DocumentVerificationBundleDocument> | null;
  merged_fields_by_role?: Record<string, any> | null;
  fields_by_role?: Record<string, any> | null;
  detail_by_role?: Record<string, any> | null;
  [key: string]: any;
}

export const vendors = createApi({
  reducerPath: "vendors",
  tagTypes: ["IDENFY", "OCR_BUNDLE"],
  baseQuery: axiosBaseQuery({
    baseUrl: VITE_API_V1_URL,
  }),
  endpoints: (builder) => ({
    idenfySearch: builder.mutation<
      { companies: any; isVendorAvailable: boolean },
      { iso3: string; name: string; reg_no: string }
    >({
      query(payload) {
        const { iso3, name, reg_no } = payload;
        const data = {
          iso3,
          company_name: name?.trim(),
          company_reg_no: reg_no?.trim()?.replace(/\s/g, ""),
        };
        return {
          url: `/search-company`,
          method: "POST",
          timeout: 10000,
          data,
        };
      },
      transformResponse: (response) => {
        return {
          companies: response?.data?.companies ?? [],
          isVendorAvailable: response.status === 200,
        };
      },

      invalidatesTags: ["IDENFY"],
    }),
    vendorAddress: builder.mutation<
      void,
      {
        iso3: string;
        registrationNumber: string;
        city: string;
        postcode: string;
        address_line1: string;
        address_line2: string;
      }
    >({
      query: (data) => {
        return {
          url: `/vendor-address`,
          method: "POST",
          data,
        };
      },
    }),
    fetchOcrData: builder.mutation<any, FetchOcrDataRequest>({
      invalidatesTags: (_result, error, { bundle_id }) =>
        !error && bundle_id ? [{ type: "OCR_BUNDLE", id: bundle_id }] : [],
      queryFn: async ({ file, extraction_mode = "hybrid_combined", source, bundle_id, document_role, onUploadComplete }, api, _extraOptions, baseQuery) => {
        const data = new FormData();
        data.append("file", file);
        data.append("extraction_mode", extraction_mode);
        data.append("async", "true");
        if (source) {
          data.append("source", source);
        }
        if (bundle_id) {
          data.append("bundle_id", bundle_id);
        }
        if (document_role) {
          data.append("document_role", document_role);
        }
        const uploaded = await baseQuery({
          url: `${VITE_API_V2_URL}/document-verification/documents/extractions`,
          data,
          method: "POST",
          timeout: 10 * 60 * 1000,
          onUploadProgress: (event) => {
            if (event.total && event.loaded >= event.total) onUploadComplete?.();
          },
        });
        if (uploaded.error) return { error: uploaded.error, meta: uploaded.meta };
        const payload = uploaded.data?.data;
        const documentId = payload?.extraction?.document_id;
        if (!documentId) return { error: { message: "The upload response did not include a document ID. Please retry." } };
        return { data: payload };
      },
    }),
    getOcrExtraction: builder.query<any, { documentId: string; extractionMode: string }>({
      query: ({ documentId, extractionMode }) => ({
        url: `${VITE_API_V2_URL}/document-verification/documents/${documentId}/extractions/${extractionMode}`,
        method: "GET",
        timeout: 30000,
      }),
      transformResponse: (response) => response?.data,
      keepUnusedDataFor: 0,
    }),
    uploadBundleSupportingDocument: builder.mutation<DocumentVerificationBundleDocument, {
      bundleId: string; requiredDocumentId: string; file: File;
    }>({
      query: ({ bundleId, requiredDocumentId, file }) => {
        const data = new FormData();
        data.append("file", file);
        data.append("required_document_id", requiredDocumentId);
        return { url: `${VITE_API_V2_URL}/document-verification/bundles/${bundleId}/documents`, method: "POST", data };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: (_result, error, { bundleId }) => error ? [] : [{ type: "OCR_BUNDLE", id: bundleId }],
    }),
    createDocumentVerificationBundle: builder.mutation<DocumentVerificationBundleResponse, DocumentVerificationBundleContext | void>({
      query: (context) => ({
        url: `${VITE_API_V2_URL}/document-verification/bundles`,
        method: "POST",
        data: context ?? {},
      }),
      transformResponse: (response) => response?.data,
    }),
    getDocumentVerificationBundle: builder.query<DocumentVerificationBundleResponse, string | { bundleId: string; includeTradeFinanceReport: boolean }>({
      providesTags: (_result, _error, context) => [{ type: "OCR_BUNDLE", id: typeof context === "string" ? context : context.bundleId }],
      query: (context) => ({
        url: `${VITE_API_V2_URL}/document-verification/bundles/${typeof context === "string" ? context : context.bundleId}`,
        method: "GET",
        params: typeof context === "string" ? undefined : { include_trade_finance_report: context.includeTradeFinanceReport },
      }),
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const {
  useIdenfySearchMutation,
  useVendorAddressMutation,
  useFetchOcrDataMutation,
  useLazyGetOcrExtractionQuery,
  useUploadBundleSupportingDocumentMutation,
  useCreateDocumentVerificationBundleMutation,
  useGetDocumentVerificationBundleQuery,
} = vendors;
